import type {
  SelfServiceCardDTO,
  TravellerBearerDTO,
} from '@/api/trafikportalen';

export const cardToBearer = (card: SelfServiceCardDTO): TravellerBearerDTO => ({
  id: card.bobcat_mtb_bearer_id,
  type: 'smartcard',
  name: card.label,
  serial: card.card_serial,
});

const hasCardSerial = (serial?: string): boolean =>
  Boolean(serial?.replace(/\s+/g, ''));

/**
 * A card that has been ordered, but not yet produced, is an alltix smartcard
 * bearer with no card serial. Type can be missing until the card exists.
 */
export const isOrderedCardBearer = (bearer: {
  type?: TravellerBearerDTO['type'];
  serial?: string;
}): boolean => {
  if (hasCardSerial(bearer.serial)) {
    return false;
  }

  return bearer.type === 'smartcard' || bearer.type === undefined;
};

export const canSetFavoredBearer = (bearer: {
  type?: TravellerBearerDTO['type'];
  serial?: string;
}): boolean => !isOrderedCardBearer(bearer);

export const getFavoredBearerLabel = (bearer: {
  name?: string;
  serial?: string;
}): string | undefined => {
  if (
    bearer.name &&
    !bearer.name.startsWith('Smartphone of traveller with id:')
  ) {
    return bearer.name;
  }

  return bearer.serial;
};

export const getTravelCardSourceBadge = (bearer: {
  type?: TravellerBearerDTO['type'];
  source?: 'alltix' | 'private';
}): 'alltix' | 'private' | undefined => {
  if (bearer.type !== 'smartcard') {
    return undefined;
  }

  return bearer.source;
};

interface ResolveVisibleBearerSelectionParams {
  visibleBearers: { id: string; default?: boolean }[];
  selectedBearerId: string;
  newlyCreatedCardId?: string;
  favoredBearerId?: string;
  favoredBearerChanged?: boolean;
}

export const resolveVisibleBearerSelection = ({
  visibleBearers,
  selectedBearerId,
  newlyCreatedCardId,
  favoredBearerId,
  favoredBearerChanged = false,
}: ResolveVisibleBearerSelectionParams): string => {
  if (visibleBearers.length === 0) {
    return '';
  }

  if (newlyCreatedCardId) {
    const newlyCreated = visibleBearers.find(
      (bearer) => bearer.id === newlyCreatedCardId,
    );
    if (newlyCreated) {
      return newlyCreated.id;
    }
  }

  if (
    favoredBearerChanged &&
    favoredBearerId &&
    visibleBearers.some((bearer) => bearer.id === favoredBearerId)
  ) {
    return favoredBearerId;
  }

  if (
    selectedBearerId &&
    visibleBearers.some((bearer) => bearer.id === selectedBearerId)
  ) {
    return selectedBearerId;
  }

  if (favoredBearerId) {
    const favored = visibleBearers.find(
      (bearer) => bearer.id === favoredBearerId,
    );
    if (favored) {
      return favored.id;
    }
  }

  const defaultBearer = visibleBearers.find((bearer) => bearer.default);
  return defaultBearer?.id ?? visibleBearers[0].id;
};
